import type { Question } from '@app/src/types/resourceExplorer'
import { ExpandLess, ExpandMore, Star, StarBorder, StarHalf } from '@mui/icons-material'
import { Collapse, Divider, IconButton, ListItem, ListItemText, Stack } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { SelectionStatus } from './types'

type QuestionRowProps = {
  question: Question
  status: SelectionStatus
  isExpanded: boolean
  onToggleExpand: () => void
  onToggleStar: () => void
  isFirst?: boolean
  showChevron?: boolean
  children?: React.ReactNode
}

const StarIcon: React.FC<{ status: SelectionStatus }> = ({ status }) => {
  if (status === SelectionStatus.All) return <Star />
  if (status === SelectionStatus.Some) return <StarHalf />
  return <StarBorder />
}

const QuestionRow: React.FC<QuestionRowProps> = ({
  question,
  status,
  isExpanded,
  onToggleExpand,
  onToggleStar,
  isFirst = false,
  showChevron = false,
  children,
}) => {
  const { formatMessage } = useIntl()
  return (
    <>
      {!isFirst && <Divider />}
      <ListItem disablePadding sx={{ px: 1, py: 1, bgcolor: 'background.paper' }}>
        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          <IconButton
            onClick={onToggleStar}
            aria-label={formatMessage({
              id:
                status === SelectionStatus.All
                  ? 'form.createAutomation.highlightImportantKpis.unstarQuestion'
                  : 'form.createAutomation.highlightImportantKpis.starQuestion',
            })}
            sx={{ color: status === SelectionStatus.None ? 'action.active' : 'primary.main' }}
          >
            <StarIcon status={status} />
          </IconButton>
          {showChevron ? (
            <Stack
              direction="row"
              alignItems="center"
              onClick={onToggleExpand}
              role="button"
              aria-expanded={isExpanded}
              aria-label={formatMessage({
                id: 'form.createAutomation.highlightImportantKpis.toggleQuestionAriaLabel',
              })}
              sx={{ flexGrow: 1, cursor: 'pointer', minHeight: 40 }}
            >
              <ListItemText
                primary={question.kpiName ?? question.questionText}
                primaryTypographyProps={{ variant: 'body2' }}
              />
              <IconButton component="div" tabIndex={-1} aria-hidden sx={{ pointerEvents: 'none' }}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>
          ) : (
            <ListItemText
              primary={question.kpiName ?? question.questionText}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          )}
        </Stack>
      </ListItem>
      {showChevron && children && (
        <Collapse in={isExpanded} sx={isExpanded ? { pb: 1 } : undefined}>
          {children}
        </Collapse>
      )}
    </>
  )
}

export default QuestionRow
